import { useEffect } from 'react';
import { Text, Box, IconButton, LucideIcon, HStack } from '@skin-seoul/design-system';
import { AnimatePresence, motion } from 'motion/react';
import { ReviewWriteForm } from './ReviewWriteForm';
import { CompleteView } from './common/CompleteView';
import BottomSheet from './common/BottomSheet';
import { useReviewForm } from '../hooks/useReviewForm';
import { useReviewModalState } from '../hooks/useReviewModalState';
import { useReviewSubmission } from '../hooks/useReviewSubmission';
import type { ReviewModalProps } from './common/interfaces';

interface DragInfo {
  offset: {
    x: number;
    y: number;
  };
  velocity: {
    x: number;
    y: number;
  };
}

const MotionBox = motion.create(Box);

const Overlay = ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
  <MotionBox
    position="fixed"
    top={0}
    left={0}
    right={0}
    bottom={0}
    backgroundColor="rgba(0, 0, 0, 0.7)"
    zIndex={999998}
    display="flex"
    alignItems="center"
    justifyContent="center"
    {...props}
  >
    {children}
  </MotionBox>
);

const MobileDrawer = ({ children, ...props }: { children?: React.ReactNode; [key: string]: unknown }) => (
  <MotionBox
    position="fixed"
    left={0}
    right={0}
    bottom={0}
    height="100dvh"
    background="white"
    borderRadius="20px 20px 0 0"
    zIndex={999999}
    overflow="hidden"
    display="flex"
    flexDirection="column"
    css={{ touchAction: 'none' }}
    {...props}
  >
    {children}
  </MotionBox>
);

export const ReviewWriteMobileModal = ({ isOpen, productInfo, onClose, onSubmit }: ReviewModalProps) => {
  const { formState, formActions, formOptions, isSubmitDisabled, resetForm } = useReviewForm({ isOpen });

  const { isComplete, setIsComplete, handleClose } = useReviewModalState({
    isOpen,
    onClose: () => {
      resetForm();
      onClose();
    },
    hasUnsavedChanges: false,
    enableConfirmation: false,
  });

  const { handleSubmit } = useReviewSubmission({
    formData: formState,
    onSubmit,
    onSubmissionComplete: () => setIsComplete(true),
  });

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  const handleDragEnd = (event: MouseEvent | TouchEvent | PointerEvent, info: DragInfo) => {
    if (info.offset.y > 200 || info.velocity.y > 200) {
      handleClose();
    }
  };

  useEffect(() => {
    if (isOpen && !isComplete) {
      setTimeout(() => {
        const originalOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        return () => {
          document.body.style.overflow = originalOverflow;
        };
      }, 100);
    }
  }, [isOpen, isComplete]);

  return (
    <AnimatePresence>
      {isOpen && !isComplete && (
        <>
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
            onClick={handleOverlayClick}
          />
          <MobileDrawer
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            drag="y"
            dragConstraints={{ top: 0, bottom: 0 }}
            onDragEnd={handleDragEnd}
            padding="24px 16px 0px"
          >
            <HStack w="full" justify="space-between" align="center" mb={4}>
              <Text textStyle="paragraph.large" fontWeight="semibold" color="neutral.800">
                Review this product
              </Text>
              <IconButton onClick={handleClose} aria-label="Close modal">
                <LucideIcon name="X" w="24px" h="24px" color="generic.100" />
              </IconButton>
            </HStack>

            <Box
              flex={1}
              overflowY="auto"
              paddingBottom="24px"
              css={{
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
                msOverflowStyle: 'none',
                scrollbarWidth: 'none',
              }}
            >
              <ReviewWriteForm
                formData={formState}
                formActions={formActions}
                options={formOptions}
                productInfo={productInfo}
                isSubmitDisabled={isSubmitDisabled}
                onSubmit={handleSubmit}
                variant="mobile"
              />
            </Box>
          </MobileDrawer>
        </>
      )}

      {isComplete && (
        <BottomSheet isOpen={isComplete} onClose={onClose} title="">
          <CompleteView onContinue={onClose} variant="mobile" />
        </BottomSheet>
      )}
    </AnimatePresence>
  );
};
